<script lang="ts" setup>
import type {RefugeImage} from '~/types/RefugeImage';

const localePath = useLocalePath();

const props = defineProps<{
  refugeImg: RefugeImage,
  text: string,
  link: string,
  // Label for the pill in the image overlay. Omit it for the generic
  // "Explore"; pass null on cards that carry their own call to action below
  // the image (/activities, /services), where a pill here would be a second
  // link to the same place.
  cta?: string | null
}>();

const loaded = ref(false);
</script>

<template>
  <nuxt-link :to="localePath(link)" class="destination-card">
    <div class="image-wrapper">
      <nuxt-img :src="refugeImg.link" :alt="refugeImg.alt" class="card-image"
                :class="{ loaded }" @load="loaded = true"
                :placeholder="[30, 30, 50, 500]"
                width="400" height="500" quality="80" format="webp" loading="lazy"/>
      <div class="overlay">
        <div class="content">
          <h3 class="title">{{ text }}</h3>
          <span v-if="cta !== null" class="explore-btn">{{ cta ?? $t('Explore') }}</span>
        </div>
      </div>
    </div>
  </nuxt-link>
</template>

<style lang="sass" scoped>
.destination-card
  position: relative
  border-radius: $border-radius-card
  overflow: hidden
  aspect-ratio: 4 / 5
  width: 100%
  max-width: 420px
  box-shadow: $shadow-modern
  transition: all $transition-standard
  cursor: pointer
  text-decoration: none

  &:hover
    transform: translateY(-10px)
    box-shadow: $shadow-hover

    .card-image
      transform: scale(1.1)

    .explore-btn
      background-color: white
      color: black
      transform: translateY(0)
      opacity: 1

    .overlay
      background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0) 100%)

.image-wrapper
  width: 100%
  height: 100%
  position: relative
  overflow: hidden

.card-image
  width: 100%
  height: 100%
  object-fit: cover
  filter: blur(20px)
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), filter 0.4s ease

  &.loaded
    filter: blur(0)

.overlay
  position: absolute
  inset: 0
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 50%)
  display: flex
  align-items: flex-end
  padding: 2.5rem
  transition: background $transition-standard

.content
  width: 100%
  display: flex
  flex-direction: column
  gap: 0.75rem

.title
  color: white
  font-size: 2.25rem
  font-weight: 800
  margin: 0
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.4)
  line-height: 1.1

.explore-btn
  display: inline-block
  width: fit-content
  padding: 0.6rem 1.5rem
  border: 2px solid rgba(255, 255, 255, 0.6)
  border-radius: 12px
  color: white
  font-size: 1rem
  font-weight: 700
  text-transform: uppercase
  letter-spacing: 1px
  transition: all $transition-standard
  opacity: 0.9
  transform: translateY(10px)

@media (max-width: 1280px)
  .destination-card
    max-width: none

@media (max-width: 480px)
  .title
    font-size: 1.75rem
  .overlay
    padding: 1.5rem
</style>
